import { curry, map } from "ramda";
import {
  AccountDescriptions,
  getAccountDescription
} from "../derivedModel/accountDescriptions";
import {
  AccountHierarchy,
  mapAccountHierarchy
} from "../derivedModel/accountHierarchy";
import {
  AccountValues,
  getAccountValuesById,
  getSummedValuesById,
  getUserEditedStatesByPeriod
} from "../derivedModel/accountValues";
import { getValuesById } from "../derivedModel/spreadFinancialValues";
import {
  getLensesById,
  SpreadFinancialValueLens,
  SpreadFinancialValues
} from "../derivedModel/spreadFinancialValues";
import {
  AccountDescription,
  AccountId,
  SpreadSheetType
} from "../spread/types";
import hiddenPresenters from "./hiddenPresenters";
import {
  AccountPresenter,
  DocumentExceptions,
  DocumentExceptionsInfo
} from "./types";

export const makeAccountPresenter = (
  children: AccountPresenter[],
  documentExceptions: DocumentExceptionsInfo[],
  periodValues: Array<number | undefined>,
  valueLensesByPeriod: SpreadFinancialValueLens[],
  accountDescription: AccountDescription,
  spreadSheetType: SpreadSheetType,
  userEditedByPeriod: boolean[]
): AccountPresenter => ({
  id: accountDescription.id,
  name: accountDescription.name,
  type: accountDescription.type,
  children,
  periodValues,
  valueLensesByPeriod,
  spreadSheetType,
  documentExceptions,
  userEditedByPeriod,
  exceptionEligible: accountDescription.exceptionEligible,
  formula: accountDescription.formula
});

const accountPresenter = (
  spreadSheetType: SpreadSheetType,
  accountDescriptions: AccountDescriptions,
  spreadSheetsAccountValues: AccountValues,
  spreadFinancialValues: SpreadFinancialValues,
  documentExceptionMap: DocumentExceptions
) => (id: AccountId, children: AccountPresenter[]) => {
  const accountDescription = getAccountDescription(accountDescriptions, id);
  const userEditedStatesByPeriod = getUserEditedStatesByPeriod(
    spreadFinancialValues,
    id
  );
  const usedChildren =
    accountDescription.type === "VALUE"
      ? hiddenPresenters(
          id,
          spreadSheetType,
          getSummedValuesById(spreadSheetsAccountValues, id),
          getAccountValuesById(spreadSheetsAccountValues),
          getLensesById(spreadFinancialValues),
          userEditedStatesByPeriod
        )
      : children;

  const documentExceptions = map(
    financialValue => documentExceptionMap[financialValue.id],
    getValuesById(spreadFinancialValues, id)
  );

  return makeAccountPresenter(
    usedChildren,
    documentExceptions,
    getAccountValuesById(spreadSheetsAccountValues, id),
    getLensesById(spreadFinancialValues, id),
    getAccountDescription(accountDescriptions, id),
    spreadSheetType,
    userEditedStatesByPeriod
  );
};

export const accountPresenters = curry(
  (
    spreadSheetType: SpreadSheetType,
    accountHierarchy: AccountHierarchy,
    accountDescriptions: AccountDescriptions,
    spreadSheetsAccountValues: AccountValues,
    spreadFinancialValues: SpreadFinancialValues,
    documentExceptions: DocumentExceptions
  ): AccountPresenter[] =>
    mapAccountHierarchy(
      accountHierarchy,
      accountPresenter(
        spreadSheetType,
        accountDescriptions,
        spreadSheetsAccountValues,
        spreadFinancialValues,
        documentExceptions
      )
    )
);

export const balanceSheetAccountPresenters = accountPresenters("BALANCE_SHEET");
export const incomeStatementAccountPresenters = accountPresenters(
  "INCOME_STATEMENT"
);
export const cashFlowAccountPresenters = accountPresenters("CASH_FLOW");



// WEBPACK FOOTER //
// ./src/spread/model/presenter/accountPresenters.ts